/**
 * How each coding agent is told about NimblyBase.
 *
 * Pure data and pure functions, deliberately kept out of the component: a
 * wrong config block is the ONLY way this page can fail, and it fails
 * silently - the person pastes it, the agent says nothing, and they conclude
 * the product does not work. So it is testable, and it is tested.
 *
 * The formats genuinely differ, and the differences are easy to get wrong:
 *
 *   - VS Code's top-level key is `servers`. Everyone else uses `mcpServers`.
 *   - Windsurf's field is `serverUrl`. Everyone else uses `url`.
 *   - Codex is TOML, not JSON, and takes the NAME of an environment
 *     variable rather than the token - which makes it the only one that
 *     never writes the credential to disk.
 *   - Claude Code has a command, so there is no file to edit at all.
 *
 * Sources are in each entry's `docs` link; they were read rather than
 * remembered.
 */

export type ClientId = 'claude-code' | 'claude-desktop' | 'cursor' | 'vscode' | 'windsurf' | 'codex' | 'json' | 'cli'

export type Snippet = {
  /** For syntax hinting and for the "copy as" affordance. */
  language: 'bash' | 'json' | 'toml' | 'text'
  text: string
}

export type ClientContext = {
  /** e.g. https://api-uat.nimblybase.com/mcp */
  url: string
  /** A real token once one has been minted; a placeholder until then. */
  token: string
  /** True when `token` is real, so the UI can warn before it is copied. */
  live: boolean
  /**
   * The client signs in for itself — V1-18.
   *
   * With OAuth there is no credential in the config at all: the client
   * reads the 401 from /mcp, finds the authorization server, registers
   * itself and asks the person to approve. So every snippet below drops
   * its Authorization header, which is the entire difference and the
   * reason this is a mode rather than a separate set of snippets.
   *
   * It is also the better path on its merits: nothing secret is written
   * to a file that gets committed, the grant is scoped rather than
   * carrying all of the person's authority, and revoking it does not
   * mean hunting for which agent holds which token.
   */
  oauth: boolean
}

export type Client = {
  id: ClientId
  name: string
  /** One line: what this client is, for somebody choosing between them. */
  blurb: string
  /**
   * Said under sign-in mode when this client cannot yet do it.
   *
   * The page promises "no credential goes in the config". Where that is
   * not true for a particular client, it has to say so on that client
   * rather than let the promise quietly cover something it does not.
   */
  signinCaveat?: string
  /** Where the file lives. Absent for clients configured by command. */
  configPath?: string
  /** UI-based connectors use instructions rather than a local config file. */
  setupTitle?: string
  /**
   * Run this first - only Codex needs it, to keep the token out of a
   * file. Returns null when the mode makes it unnecessary.
   */
  preamble?(ctx: ClientContext): Snippet | null
  /** The thing to copy. */
  snippet(ctx: ClientContext): Snippet
  docs: string
}

export const TOKEN_PLACEHOLDER = '<paste-your-token>'

/** The env var Codex reads the token from. Also what the CLI honours. */
export const TOKEN_ENV = 'NIMBLYBASE_TOKEN'

const SERVER_NAME = 'nimblybase'

const pretty = (value: unknown) => JSON.stringify(value, null, 2)

/**
 * The header, or nothing at all.
 *
 * One helper rather than a conditional in every entry, because the
 * failure this file exists to prevent is a config block that is subtly
 * wrong for one client - and "this one still has a stale header" is
 * exactly that shape.
 */
const authHeader = (token: string, oauth: boolean) =>
  (oauth ? {} : { headers: { Authorization: `Bearer ${token}` } })

export const CLIENTS: Client[] = [
  {
    id: 'claude-code',
    name: 'Claude Code',
    blurb: 'Anthropic’s terminal agent. One command, no file to edit.',
    docs: 'https://docs.claude.com/en/docs/claude-code/mcp',
    snippet: ({ url, token, oauth }) => ({
      language: 'bash',
      text: oauth
        ? `claude mcp add --transport http ${SERVER_NAME} ${url}`
        : `claude mcp add --transport http ${SERVER_NAME} ${url} \\\n  --header "Authorization: Bearer ${token}"`,
    }),
  },

  {
    id: 'claude-desktop',
    name: 'Claude Desktop',
    blurb: 'Add a remote connector in Claude’s Connectors settings; it connects from Anthropic’s cloud.',
    docs: 'https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp',
    configPath: 'Customize → Connectors → Add → Add custom connector',
    setupTitle: 'Add a custom connector',
    signinCaveat: 'Connector access depends on your Claude plan and team settings. For OAuth, choose Register automatically. A remote connector also needs a separate repository/build route to ship your frontend.',
    snippet: ({ url, token, oauth }) => ({
      language: 'text',
      text: oauth
        ? `Name: ${SERVER_NAME}\nRemote MCP URL: ${url}\nContinue, choose Sign in now → Register automatically, then Connect.`
        : `Name: ${SERVER_NAME}\nRemote MCP URL: ${url}\nRequest headers: Authorization: Bearer ${token}\nAdd the connector, then Connect.`,
    }),
  },

  {
    id: 'cursor',
    name: 'Cursor',
    blurb: 'Remote servers take a url and headers.',
    docs: 'https://cursor.com/docs/mcp',
    configPath: '~/.cursor/mcp.json  (or .cursor/mcp.json for one project)',
    snippet: ({ url, token, oauth }) => ({
      language: 'json',
      text: pretty({
        mcpServers: {
          [SERVER_NAME]: { url, ...authHeader(token, oauth) },
        },
      }),
    }),
  },

  {
    id: 'vscode',
    name: 'VS Code',
    blurb: 'Note the top-level key is "servers", not "mcpServers".',
    docs: 'https://code.visualstudio.com/docs/agents/reference/mcp-configuration',
    configPath: '.vscode/mcp.json  (or MCP: Open User Configuration)',
    snippet: ({ url, token, live, oauth }) => ({
      language: 'json',
      // With a real token, inline it - the person asked for it. Without one,
      // show the input-variable form, which is what VS Code's own docs
      // recommend and which prompts rather than storing a secret in a file
      // that tends to get committed.
      text: oauth
        ? pretty({ servers: { [SERVER_NAME]: { type: 'http', url } } })
        : live
        ? pretty({
            servers: {
              [SERVER_NAME]: { type: 'http', url, headers: { Authorization: `Bearer ${token}` } },
            },
          })
        : pretty({
            inputs: [
              { type: 'promptString', id: 'nimblybase-token', description: 'NimblyBase access token', password: true },
            ],
            servers: {
              [SERVER_NAME]: {
                type: 'http', url,
                headers: { Authorization: 'Bearer ${input:nimblybase-token}' },
              },
            },
          }),
    }),
  },

  {
    id: 'windsurf',
    name: 'Windsurf',
    blurb: 'Note the field is "serverUrl", not "url".',
    docs: 'https://docs.windsurf.com/windsurf/cascade/mcp',
    configPath: '~/.codeium/windsurf/mcp_config.json',
    snippet: ({ url, token, oauth }) => ({
      language: 'json',
      text: pretty({
        mcpServers: {
          [SERVER_NAME]: { serverUrl: url, ...authHeader(token, oauth) },
        },
      }),
    }),
  },

  {
    id: 'codex',
    name: 'Codex CLI',
    blurb: 'TOML, and it reads the token from the environment - so nothing secret is written to a file.',
    docs: 'https://developers.openai.com/codex/mcp',
    configPath: '~/.codex/config.toml',
    // No preamble under OAuth: there is no token to put anywhere, which
    // makes the one client that never wrote a secret to disk the one
    // that now needs no secret at all.
    preamble: ({ token, oauth }) => (oauth ? null : {
      language: 'bash',
      text: `export ${TOKEN_ENV}="${token}"`,
    }),
    snippet: ({ url, oauth }) => ({
      language: 'toml',
      text: oauth
        ? `[mcp_servers.${SERVER_NAME}]\nurl = "${url}"`
        : `[mcp_servers.${SERVER_NAME}]\nurl = "${url}"\nbearer_token_env_var = "${TOKEN_ENV}"`,
    }),
  },

  {
    id: 'json',
    name: 'MCP JSON',
    blurb: 'The shape most clients accept. Start here for anything not listed.',
    docs: 'https://modelcontextprotocol.io/docs/concepts/transports',
    snippet: ({ url, token, oauth }) => ({
      language: 'json',
      text: pretty({
        mcpServers: {
          [SERVER_NAME]: { type: 'http', url, ...authHeader(token, oauth) },
        },
      }),
    }),
  },

  {
    id: 'cli',
    name: 'CLI',
    blurb: 'Not an agent - you, in a terminal. Browser sign-in grants the same scoped permissions.',
    docs: 'https://www.npmjs.com/package/nimblybase',
    preamble: () => ({ language: 'bash', text: 'npm install -g nimblybase' }),
    snippet: ({ token, live, oauth }) => ({
      language: 'bash',
      // `nimbly login` with no argument is the browser flow. It is the
      // default here because it is what somebody at a terminal wants.
      text: oauth || !live
        ? 'nimbly login\nnimbly projects'
        : `nimbly login --token ${token}\nnimbly projects`,
    }),
  },
]

export const clientById = (id: string): Client | undefined => CLIENTS.find((c) => c.id === id)

/**
 * The two prompts the SRS asks for, kept together because they are a pair:
 * one proves the connection, the other uses it.
 */
export const VERIFY_PROMPT =
  'List my NimblyBase projects.'

export const BUILD_PROMPT =
  'Build a private notes app with sign-in, attachments and live updates using NimblyBase. '
  + 'Inspect this repository and assess compatibility first. If suitable, create a project only when needed; '
  + 'show me the architecture and schema plan before applying it, then give me a working URL. '
  + 'Verify notes persist in the backend and each user can access only their own notes and files.'

/** e.g. https://api-uat.nimblybase.com -> https://api-uat.nimblybase.com/mcp */
export const mcpUrl = (apiBase: string): string => `${apiBase.replace(/\/+$/, '')}/mcp`
